import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import Modal1 from "../DIsplay/Modal/Modal1";
import OrderStatus from "../order/OrderStatus";
import EsimBookingSummaryCard from "../esim/EsimBookingSummaryCard";
import EsimInfoSections from "../esim/EsimInfoSections";
import getEsimBooking from "../../controllers/esim/getEsimBooking";
import {
  formatPackagePrice,
  getEsimDisplaySections,
  normalizeEsimBookingResponse,
} from "../../utils/esim";

function formatPriceWithoutCode(amount, currency) {
  return formatPackagePrice(amount, currency).replace(/\s+[A-Z]{3}$/, "");
}

/**
 * Admin eSIM order detail modal — mirrors miles confirmation content
 * without installation guide / SIM usage actions.
 */
export function EsimOrderDetailsModal({ open, setOpen, booking }) {
  const bookingId = booking?._id || booking?.id;
  const [loading, setLoading] = useState(false);
  const [bookingResponse, setBookingResponse] = useState(null);

  useEffect(() => {
    let active = true;

    async function load() {
      if (!open || !bookingId) {
        setBookingResponse(null);
        return;
      }

      setLoading(true);
      const res = await getEsimBooking(bookingId);
      if (!active) return;

      if (res?.return) {
        const payload = res.data;
        const wrapped =
          payload?.esimBooking ||
          payload?.esim_booking ||
          (payload?.booking && payload?.packageDetails)
            ? payload
            : {
                esimBooking: payload,
                booking: payload?.mainBooking || payload?.booking,
              };
        setBookingResponse(normalizeEsimBookingResponse(wrapped));
      } else {
        enqueueSnackbar(res?.msg || "Failed to load eSIM booking", {
          variant: "error",
        });
        setBookingResponse(null);
      }
      setLoading(false);
    }

    load();
    return () => {
      active = false;
    };
  }, [open, bookingId]);

  const details = bookingResponse?.packageDetails;
  const esimBooking = bookingResponse?.esimBooking;
  const mainBooking = bookingResponse?.booking;
  const status = esimBooking?.status || details?.status || booking?.status;
  const displayBookingId =
    mainBooking?.bookingId ||
    esimBooking?.bookingId ||
    booking?.bookingId ||
    bookingId;

  const account =
    esimBooking?.account ||
    mainBooking?.account ||
    booking?.account ||
    booking?.mainBooking?.account;
  const agentName = account
    ? [account?.firstName, account?.lastName].filter(Boolean).join(" ") ||
      account?.email ||
      account?._id
    : null;

  const infoSections = useMemo(
    () => getEsimDisplaySections(details),
    [details],
  );

  return (
    <Modal1 open={open} setOpen={setOpen}>
      <div className="w-[92vw] max-w-[920px] max-h-[85vh] overflow-y-auto scroll-hide bg-[#F5F8FF]">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-[#E6E8EC] bg-white px-4 py-3 sm:px-5">
          <h2 className="text-base font-bold text-[#1E305A] sm:text-lg">
            eSIM order detail
          </h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md p-1 text-[#6D7C94] hover:bg-[#F0F6FC] hover:text-primary"
            aria-label="Close eSIM order detail"
          >
            <Icon icon="mdi:close" className="text-2xl" />
          </button>
        </div>

        {loading ? (
          <div className="flex min-h-[220px] items-center justify-center p-6">
            <CircularProgress />
          </div>
        ) : !details ? (
          <div className="flex min-h-[180px] flex-col items-center justify-center gap-2 p-6 text-center">
            <span className="text-base font-semibold text-[#1E305A]">
              eSIM booking not found
            </span>
            <span className="text-sm text-slate-500">
              Unable to load package details for this order.
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-4 sm:p-5">
            <div className="flex flex-col gap-3 rounded-xl border border-white bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div>
                <span className="block text-xs font-semibold uppercase tracking-[0.28em] text-[#1E61DC]">
                  eSIM confirmation
                </span>
                <h1 className="mt-1 text-lg font-bold text-[#1E305A] sm:text-xl">
                  {details.title || "eSIM booking"}
                </h1>
                <span className="mt-1 block text-sm text-slate-500">
                  Booking ID:{" "}
                  <span className="font-semibold text-[#1E305A]">
                    {displayBookingId}
                  </span>
                </span>
                {agentName ? (
                  <span className="mt-1 block text-sm text-slate-500">
                    Agent:{" "}
                    <span className="font-semibold text-[#1E305A]">
                      {agentName}
                    </span>
                  </span>
                ) : null}
              </div>
              <div className="flex flex-col items-start gap-2 sm:items-end">
                {status ? <OrderStatus value={status} /> : null}
                <span className="text-lg font-bold text-[#1E305A]">
                  {formatPriceWithoutCode(
                    details.grandTotal || details.displayPrice,
                    details.currency,
                  )}
                </span>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
              <EsimInfoSections sections={infoSections} />
              <EsimBookingSummaryCard bookingResponse={bookingResponse} />
            </div>
          </div>
        )}
      </div>
    </Modal1>
  );
}

export default EsimOrderDetailsModal;
